import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { format } from "date-fns";
import { 
  Users, 
  Calendar, 
  Music2, 
  Bell, 
  ChevronRight,
  MapPin,
  Clock,
  Check,
  X,
  HelpCircle,
  Crown,
  AlertTriangle,
  ArrowUpRight,
  CalendarDays,
  Layers,
  Edit2
} from "lucide-react";
import { toast } from "sonner";
import { BandLogo } from "../components/MediaImages";
import { PushPermissionBanner } from "../components/PushPermissionBanner";
import AchievementsCard from "../components/AchievementsCard";
import QuickRsvp from "../components/QuickRsvp";
import { useStandaloneMode } from "../hooks/useStandaloneMode";
import usePullToRefresh from "../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../components/PullToRefreshIndicator";

const Dashboard = () => {
  const isStandalone = useStandaloneMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter-66g: RSVP state simplified — `stats.upcoming_events[].my_status` is the
  // source of truth, mutated optimistically via QuickRsvp's onChange.
  const [allBandsSchedule, setAllBandsSchedule] = useState({ events: [], bands: [] });

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  useEffect(() => {
    fetchDashboardStats();
    fetchAllBandsSchedule();
    initDefaultSections();
  }, []);

  const fetchAllBandsSchedule = async () => {
    try {
      const res = await apiClient.get("/calendar/all-bands");
      setAllBandsSchedule(res.data || { events: [], bands: [] });
    } catch (e) {
      // Single-band users get a 404 here — expected, so we don't toast,
      // just log for debugging.
      console.error("fetchAllBandsSchedule failed:", e);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await apiClient.get("/dashboard/stats");
      setStats(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Iter-66g: optimistic local update — keeps RSVP click instant and in sync
  // with the shared <QuickRsvp> component.
  const onRsvpChange = (eventId, status) => {
    setStats((prev) => {
      if (!prev?.upcoming_events) return prev;
      return {
        ...prev,
        upcoming_events: prev.upcoming_events.map((e) =>
          e.id === eventId ? { ...e, my_status: status } : e
        ),
      };
    });
  };

  const initDefaultSections = async () => {
    try {
      await apiClient.post("/sections/init-defaults");
    } catch (error) {
      // Idempotent endpoint — 409s when sections already exist. Log for
      // debugging but don't surface.
      console.error("initDefaultSections failed:", error);
    }
  };

  // Iter-67: pull-to-refresh on mobile. Pulls re-fetch dashboard stats and
  // the multi-band schedule in parallel.
  const handleRefresh = async () => {
    await Promise.all([fetchDashboardStats(), fetchAllBandsSchedule()]);
  };
  const { pullDistance, phase, threshold } = usePullToRefresh(handleRefresh);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="player-dashboard" className="space-y-6 animate-fade-in">
      <PullToRefreshIndicator pullDistance={pullDistance} phase={phase} threshold={threshold} />
      <PushPermissionBanner />
      {/* Welcome Header — compact mobile layout only when running as an
          installed PWA. Browser visitors keep the original full hero. */}
      <div className={`relative overflow-hidden rounded-2xl premium-card ${isStandalone ? "p-4 sm:p-8" : "p-5 sm:p-8"}`}>
        <div className="spotlight absolute inset-0 pointer-events-none" />
        <div className="relative z-10 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
            <BandLogo
              bandId={user?.band_id}
              className={`${isStandalone ? "w-10 h-10 sm:w-14 sm:h-14 hidden sm:block" : "w-12 h-12 sm:w-14 sm:h-14"} rounded-xl flex-shrink-0`}
              fallback={null}
            />
            <div className="min-w-0 flex-1">
              <p className={`overline ${isStandalone ? "mb-0.5 text-[10px] sm:text-xs" : "mb-1"}`}>Welcome back</p>
              <h1 className={`${isStandalone ? "text-xl sm:text-3xl lg:text-4xl" : "text-2xl sm:text-3xl lg:text-4xl"} font-heading font-bold tracking-tight text-foreground break-words`}>
                {user?.name}
              </h1>
              <p className={`text-muted-foreground ${isStandalone ? "mt-1" : "mt-1.5"} text-xs sm:text-sm break-words`}>
                {user?.role === "admin" ? "Band Administrator" : "Band Member"} • {user?.band_name}
                {isStandalone && user?.subscription_status === 'trial' && (
                  <span className="sm:hidden"> · <span className={user?.days_remaining <= 7 ? 'text-orange-400' : 'text-blue-400'}>
                    {user?.days_remaining}d trial
                  </span></span>
                )}
              </p>
            </div>
          </div>
          <Badge 
            variant="outline" 
            className="border-primary/20 bg-primary/5 text-primary font-semibold text-[10px] sm:text-[11px] uppercase tracking-wider flex-shrink-0"
          >
            {user?.role}
          </Badge>
        </div>
      </div>

      {/* Trial banner — full version for browser visitors at every width, and
          for installed-PWA users only above sm: (mobile standalone shows the
          inlined trial pill in the welcome subtitle above instead). */}
      {user?.subscription_status === 'trial' && (
        <div className={`${isStandalone ? "hidden sm:flex" : "flex"} rounded-xl p-4 items-center justify-between flex-wrap gap-4 ${
          user?.days_remaining <= 7 
            ? 'bg-orange-500/10 border border-orange-500/20' 
            : 'bg-blue-500/10 border border-blue-500/20'
        }`}>
          <div className="flex items-center gap-3">
            {user?.days_remaining <= 7 ? (
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-blue-400" />
              </div>
            )}
            <div>
              <h3 className={`font-semibold text-sm ${user?.days_remaining <= 7 ? 'text-orange-400' : 'text-blue-400'}`}>
                {user?.days_remaining <= 7 ? 'Trial Ending Soon' : 'Free Trial Active'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {user?.days_remaining} days remaining
              </p>
            </div>
          </div>
          <Link to="/subscription">
            <Button 
              size="sm" 
              className={user?.days_remaining <= 7 
                ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30'
              }
            >
              {user?.days_remaining <= 7 ? 'Subscribe Now' : 'View Plans'}
              <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      {/* iter-119: distinguish "grace" (read-write, urgent) from "read_only"
          (locked) instead of treating both as "expired". */}
      {user?.effective_subscription_status === 'grace' && (
        <div className="rounded-xl p-4 flex items-center justify-between flex-wrap gap-4 bg-orange-500/10 border border-orange-500/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-orange-400">Trial ended &mdash; {user?.grace_days_remaining ?? 0} day{user?.grace_days_remaining === 1 ? '' : 's'} of grace left</h3>
              <p className="text-xs text-muted-foreground">After that, editing will be locked. Pick a plan now to avoid interruption.</p>
            </div>
          </div>
          <Link to="/subscription">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
              Choose a plan
              <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      {user?.effective_subscription_status === 'read_only' && (
        <div className="rounded-xl p-4 flex items-center justify-between flex-wrap gap-4 bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-red-400">Editing locked &mdash; trial ended</h3>
              <p className="text-xs text-muted-foreground">Everything is read-only until you pick a plan. Members can still view; admins choose a plan to unlock editing.</p>
            </div>
          </div>
          <Link to="/subscription">
            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white">
              Choose a plan
              <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className={`grid gap-4 ${showAdminControls ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'}`}>
        {showAdminControls && (
          <>
            <div className="stat-card rounded-xl p-3 sm:p-5 hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Total Players</p>
                  <p className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{stats?.total_players || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
            </div>

            <div className="stat-card rounded-xl p-3 sm:p-5 hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Sections</p>
                  <p className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{stats?.total_sections || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-primary" />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="stat-card rounded-xl p-3 sm:p-5 hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Rehearsals</p>
              <p className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{stats?.upcoming_rehearsals || 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">upcoming</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="stat-card rounded-xl p-3 sm:p-5 hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Concerts</p>
              <p className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{stats?.upcoming_concerts || 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">upcoming</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-velvet-red/10 flex items-center justify-center">
              <Music2 className="w-5 h-5 text-velvet-red" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Upcoming Events */}
        <div className="lg:col-span-2">
          <div className="glass-card h-full">
            <div className="flex items-center justify-between p-5 border-b border-white/[0.04]">
              <div>
                <h2 className="font-heading text-lg font-semibold">Upcoming Events</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Your schedule for the next 30 days</p>
              </div>
              <Link to="/events">
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10 h-8 text-xs font-semibold">
                  View All <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="p-5">
              {stats?.upcoming_events?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcoming_events.slice(0, 5).map((event, index) => (
                    <div 
                      key={event.id} 
                      className="p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-all duration-200 stagger-item border border-transparent hover:border-white/[0.04]"
                      data-testid={`dashboard-event-${event.id}`}
                    >
                      <div 
                        className="flex items-start gap-4 cursor-pointer"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          event.event_type === 'concert' 
                            ? 'bg-gradient-to-br from-velvet-red/30 to-velvet-red/10' 
                            : 'bg-gradient-to-br from-blue-500/30 to-blue-500/10'
                        }`}>
                          {event.event_type === 'concert' ? (
                            <Music2 className="w-5 h-5 text-red-400" />
                          ) : (
                            <Calendar className="w-5 h-5 text-blue-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                            <h3 className="font-semibold text-sm break-words flex-1 min-w-0">{event.title}</h3>
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] px-1.5 py-0 h-5 font-medium flex-shrink-0 ${
                                event.event_type === 'concert' 
                                  ? 'border-velvet-red/30 text-red-400 bg-velvet-red/10' 
                                  : 'border-blue-400/30 text-blue-400 bg-blue-500/10'
                              }`}
                            >
                              {event.event_type}
                            </Badge>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{format(new Date(event.start_time), "EEE, MMM d • h:mm a")}</span>
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1 min-w-0">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="break-words">{event.location}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Quick RSVP — shared component, iter-66g */}
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-3 pt-3 border-t border-white/[0.04] flex-wrap">
                        <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mr-1">RSVP:</span>
                        <QuickRsvp event={event} onChange={onRsvpChange} hidden={showAdminControls} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-14 h-14 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-7 h-7 text-muted-foreground/50" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">No upcoming events</p>
                  <p className="text-xs text-muted-foreground/70">Your schedule is clear</p>
                  {user?.role === "admin" && (
                    <Link to="/events">
                      <Button variant="outline" size="sm" className="mt-4 h-8 text-xs">
                        Schedule an Event
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: My Schedule + Notifications */}
        <div className="space-y-5">
          {/* Achievements summary — hidden for brand-new users with 0 attendance */}
          <AchievementsCard />

          {/* My Schedule across all bands — only show if user belongs to >1 band */}
          {allBandsSchedule.bands.length > 1 && (
            <div className="glass-card" data-testid="my-schedule-all-bands-card">
              <div className="flex items-center justify-between p-5 border-b border-white/[0.04]">
                <div>
                  <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" />
                    My Schedule
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Next 3 across your {allBandsSchedule.bands.length} bands
                  </p>
                </div>
                <Link to="/calendar/all">
                  <Button variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10 h-8 text-xs font-semibold">
                    Calendar <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="p-5">
                {(() => {
                  const now = new Date();
                  const upcoming = (allBandsSchedule.events || [])
                    .filter((e) => new Date(e.start_time) >= now)
                    .slice(0, 3);
                  if (upcoming.length === 0) {
                    return (
                      <div className="text-center py-10">
                        <div className="w-12 h-12 rounded-xl bg-secondary/50 flex items-center justify-center mx-auto mb-3">
                          <CalendarDays className="w-6 h-6 text-muted-foreground/50" />
                        </div>
                        <p className="text-sm text-muted-foreground">No upcoming events across your bands</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-3">
                      {upcoming.map((e) => {
                        const cancelled = (e.status || "").toLowerCase() === "cancelled";
                        return (
                          <button
                            key={e.id}
                            onClick={() => navigate(`/events/${e.id}`)}
                            className="w-full text-left p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition border border-transparent hover:border-white/[0.04]"
                            data-testid={`my-schedule-event-${e.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                e.event_type === "concert"
                                  ? "bg-gradient-to-br from-velvet-red/30 to-velvet-red/10"
                                  : "bg-gradient-to-br from-blue-500/30 to-blue-500/10"
                              }`}>
                                {e.event_type === "concert" ? (
                                  <Music2 className="w-4 h-4 text-red-400" />
                                ) : (
                                  <Calendar className="w-4 h-4 text-blue-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0.5">
                                  <p className={`font-medium text-sm break-words flex-1 min-w-0 ${cancelled ? "line-through opacity-70" : ""}`}>
                                    {e.title}
                                  </p>
                                  {cancelled && (
                                    <Badge variant="outline" className="bg-red-500/15 text-red-400 border-transparent text-[10px] flex-shrink-0">Cancelled</Badge>
                                  )}
                                </div>
                                <p className="text-[11px] text-primary font-medium break-words">{e.band_name}</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                                  {format(new Date(e.start_time), "EEE, MMM d • h:mm a")}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Recent Notifications */}
          <div className="glass-card h-full">
            <div className="flex items-center justify-between p-5 border-b border-white/[0.04]">
              <div>
                <h2 className="font-heading text-lg font-semibold">Notifications</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Recent updates</p>
              </div>
              <Link to="/notifications">
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10 h-8 text-xs font-semibold">
                  All <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="p-5">
              {stats?.recent_notifications?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_notifications.map((notification, index) => (
                    <div 
                      key={notification.id}
                      className="p-3.5 rounded-xl bg-secondary/30 stagger-item"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Bell className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{notification.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70 mt-1.5 font-mono">
                            {format(new Date(notification.sent_at), "MMM d, h:mm a")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-12 h-12 rounded-xl bg-secondary/50 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-6 h-6 text-muted-foreground/50" />
                  </div>
                  <p className="text-sm text-muted-foreground">No notifications</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
